import type { FC, ReactNode } from "react"
import { Link } from "react-router"

import { useActiveOrganizationId } from "@/contexts"
import type { Lab } from "@/models/lab"
import { RootPath } from "@/models/route"
import { getAuthenticatedRoutePath } from "@/utils/url"

import ElectricMeterIcon from "@mui/icons-material/ElectricMeter"
import ScienceIcon from "@mui/icons-material/Science"
import { Box, Paper, Typography, styled } from "@mui/material"
import type { PaperProps } from "@mui/material"
import { alpha } from "@mui/material/styles"

const webAddressPattern = /^https?:\/\//i

const isLabWebAddress = (link: string): boolean => webAddressPattern.test(link)

const stripLeadingSlashes = (link: string): string => link.replace(/^\/+/, "")

const getLabPlaceholderIcon = (link: string): typeof ScienceIcon =>
  stripLeadingSlashes(link) === RootPath.Neat ? ElectricMeterIcon : ScienceIcon

const resolveLabHref = (link: string, organizationId: string): string => {
  if (isLabWebAddress(link)) {
    return link
  }

  return getAuthenticatedRoutePath(organizationId, stripLeadingSlashes(link))
}

const LabCardRoot = styled(Paper)<PaperProps>(({ theme }) => ({
  color: "inherit",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: 0,
  textDecoration: "none",
  width: theme.typography.pxToRem(320),
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1,
  },
})) as typeof Paper

interface LabCardProps {
  lab: Lab
}

const LabCardBody = ({ lab }: LabCardProps): ReactNode => {
  return (
    <>
      {lab.image ? (
        <Box
          alt={lab.title}
          component="img"
          src={lab.image}
          sx={{
            backgroundImage: (theme) =>
              `linear-gradient(165deg, ${theme.palette.grey[200]} 10%, ${theme.palette.grey[400]} 85%)`,
            display: "block",
            height: (theme) => theme.typography.pxToRem(128),
            objectFit: "cover",
            objectPosition: "center",
            width: "100%",
          }}
        />
      ) : (
        <Box
          sx={{
            alignItems: "center",
            backgroundImage: (theme) =>
              `linear-gradient(165deg, ${theme.palette.spectrum.blue[400]} 10%, ${theme.palette.spectrum.blue[600]} 85%)`,
            display: "flex",
            height: (theme) => theme.typography.pxToRem(128),
            justifyContent: "center",
          }}
        >
          <Box
            aria-hidden
            component={getLabPlaceholderIcon(lab.link)}
            sx={{
              color: "common.white",
              filter: (theme) =>
                `drop-shadow(0 ${theme.spacing(0.25)} ${theme.spacing(0.25)} ${alpha(theme.palette.common.black, 0.1)})`,
              fontSize: (theme) => theme.typography.pxToRem(128),
            }}
          />
        </Box>
      )}
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{lab.title}</Typography>
        <Typography>{lab.description}</Typography>
      </Box>
    </>
  )
}

export const LabCard: FC<LabCardProps> = ({ lab }) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const href = resolveLabHref(lab.link, activeOrganizationId ?? "")

  if (isLabWebAddress(lab.link)) {
    return (
      <LabCardRoot
        aria-label={lab.title}
        component="a"
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        <LabCardBody lab={lab} />
      </LabCardRoot>
    )
  }

  return (
    <LabCardRoot aria-label={lab.title} component={Link} to={href}>
      <LabCardBody lab={lab} />
    </LabCardRoot>
  )
}
